import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { AuthContext } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { UserAPI, StatsAPI } from '../services/api';
import { 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiCalendar,
  FiLock,
  FiEye,
  FiEyeOff,
  FiEdit2,
  FiSave,
  FiX,
  FiShield,
  FiGlobe,
  FiCreditCard,
  FiLogOut,
  FiCamera,
} from 'react-icons/fi';

const ProfilePage = () => {
  const { user: authUser, updateUser, changePassword, logout } = useContext(AuthContext);
  const { 
    getFormattedBalances,
    isLoading: walletLoading,
    error: walletError,
    fetchBalance 
  } = useWallet();
  
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // User data from backend
  const [userData, setUserData] = useState(null);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    country: '',
    date_of_birth: ''
  });

  // User stats (if you have stats API)
  const [userStats, setUserStats] = useState({
    totalBets: 0,
    winRate: 0,
    totalWagered: 0,
    totalWinnings: 0,
    favoriteSport: 'N/A',
    averageOdds: 0,
    longestWinStreak: 0,
    biggestWin: 0
  });

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({});
  const navigate = useNavigate();

  // Get formatted balances from wallet context
  const formattedBalances = getFormattedBalances();

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Fetch user profile
        const profileResponse = await UserAPI.getProfile();
        // console.log('Profile API response:', profileResponse);
        
        if (profileResponse && profileResponse.success !== false) {
          setUserData(profileResponse);
          setFormData({
            full_name: profileResponse.full_name || '',
            phone_number: profileResponse.phone_number || '',
            country: profileResponse.country || '',
            date_of_birth: profileResponse.date_of_birth || ''
          });
        }
        
      } catch (error) {
        // console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchUserData();
    } else {
      navigate('/login');
    }
  }, [authUser]);

  // Refresh wallet data on page load
  useEffect(() => {
    const refreshWalletData = async () => {
      if (authUser && !walletLoading) {
        try {
          await fetchBalance();
        } catch (error) {
          // console.error('Failed to refresh wallet on profile page:', error);
        }
      }
    };
    
    refreshWalletData();
  }, [authUser, walletLoading, fetchBalance]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[A-Z])(?=.*[0-9])/.test(passwordForm.newPassword)) {
      errors.newPassword = 'Password must contain uppercase and number';
    }
    
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };

  const handlePasswordSubmit = async () => {
    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    try {
      setSaving(true);
      const result = await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      
      if (result.success) {
        alert('Password updated successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordErrors({});
      } else {
        setPasswordErrors({ 
          currentPassword: result.error || 'Failed to update password' 
        });
      }
    } catch (error) {
      // console.error('Password change error:', error);
      setPasswordErrors({ 
        currentPassword: 'Failed to update password. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSave = async () => {
    try {
      setSaving(true);
      const result = await updateUser(formData);
      
      if (result.success) {
        // Update local user data
        setUserData(prev => ({ ...prev, ...formData }));
        setIsEditing(false);
        alert('Profile updated successfully!');
      } else {
        alert(result.error || 'Failed to update profile');
      }
    } catch (error) {
      // console.error('Profile update error:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setIsUploading(true);
      // TODO: Implement avatar upload API
      setTimeout(() => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setIsUploading(false);
        };
        reader.readAsDataURL(file);
      }, 1500);
    }
  };

  const getVerificationStatus = () => {
    if (!userData) return { text: 'Loading...', color: 'text-gray-400', percent: 0, icon: '⚪' };
    
    switch(userData.kyc_status) {
      case 'verified': return { text: 'Verified', color: 'text-green-400', percent: 100, icon: '🟢' };
      case 'pending': return { text: 'Pending', color: 'text-yellow-400', percent: 66, icon: '🟡' };
      case 'rejected': return { text: 'Rejected', color: 'text-red-400', percent: 33, icon: '🔴' };
      default: return { text: 'Not Submitted', color: 'text-gray-400', percent: 0, icon: '⚪' };
    }
  };

  const verificationStatus = getVerificationStatus();

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getAccountLevel = () => {
    // You can implement account level logic based on activity, deposits, etc.
    return {
      level: 'Standard',
      color: 'text-gray-400'
    };
  };

  const accountLevel = getAccountLevel();

  const handleWalletRefresh = async () => {
    try {
      await fetchBalance();
    } catch (error) {
      // console.error('Failed to refresh wallet:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-red-500 text-xl mb-4">Error loading profile data</div>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">👤 My Profile</h1>
            <p className="text-gray-400 text-sm">Manage your account settings and preferences</p>
          </div>
          
          {/* Edit/Save Button */}
          {activeTab === 'personal' && (
            <button
              onClick={() => isEditing ? handleProfileSave() : setIsEditing(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </>
              ) : (
                <>
                  <FiEdit2 className="w-4 h-4" />
                  Edit Profile
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Column - Profile Card */}
          <div className="lg:w-1/3">
            {/* Profile Card */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6 mb-4">
              {/* Avatar Section */}
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 mx-auto mb-4 flex items-center justify-center text-white text-3xl sm:text-4xl">
                    {userData.full_name ? (
                      <>{userData.full_name.split(' ').map(n => n[0]).join('')}</>
                    ) : (
                      <>{userData.username?.charAt(0).toUpperCase() || 'U'}</>
                    )}
                  </div>
                  
                  {/* Upload Button (for future implementation) */}
                  <label className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full cursor-pointer transition-colors">
                    <FiCamera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      disabled
                    />
                  </label>
                  
                  {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {userData.full_name || userData.username || 'User'}
                </h2>
                <p className="text-gray-400">@{userData.username || 'username'}</p>
                <div className={`mt-2 inline-flex items-center bg-gray-800 px-3 py-1 rounded-full text-sm ${accountLevel.color}`}>
                  {accountLevel.level} Member
                </div>
              </div>

              {/* Verification Status */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">KYC Status</span>
                  <span className={`font-bold ${verificationStatus.color}`}>
                    {verificationStatus.icon} {verificationStatus.text}
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      userData.kyc_status === 'verified' ? 'bg-green-500' :
                      userData.kyc_status === 'pending' ? 'bg-yellow-500' :
                      userData.kyc_status === 'rejected' ? 'bg-red-500' :
                      'bg-gray-600'
                    }`}
                    style={{ width: `${verificationStatus.percent}%` }}
                  ></div>
                </div>
                <div className="text-gray-400 text-sm mt-2">
                  {userData.kyc_status === 'not_submitted' ? (
                    <button 
                      onClick={() => navigate('/kyc')}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      Complete KYC for higher limits
                    </button>
                  ) : userData.kyc_status === 'rejected' ? (
                    <button 
                      onClick={() => navigate('/kyc')}
                      className="text-red-400 hover:text-red-300"
                    >
                      Resubmit KYC
                    </button>
                  ) : (
                    'KYC Status'
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    <FiCalendar className="inline mr-2" />
                    Member Since
                  </span>
                  <span className="text-white">{formatDate(userData.created_at)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    <FiGlobe className="inline mr-2" />
                    Country
                  </span>
                  <span className="text-white">{userData.country || 'Not set'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    <FiMail className="inline mr-2" />
                    Email Verified
                  </span>
                  <span className={`${userData.email_verified ? 'text-green-400' : 'text-red-400'}`}>
                    {userData.email_verified ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    <FiCreditCard className="inline mr-2" />
                    Referral Code
                  </span>
                  <span className="text-white font-mono">{userData.referral_code || 'N/A'}</span>
                </div>
              </div>

              {/* Wallet Balance */}
              <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-white">💰 Wallet Balance</h4>
                  <button
                    onClick={handleWalletRefresh}
                    disabled={walletLoading}
                    className="text-blue-400 hover:text-blue-300 text-sm disabled:opacity-50"
                  >
                    {walletLoading ? (
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Refresh'
                    )}
                  </button>
                </div>
                {walletError ? (
                  <div className="text-center py-4">
                    <div className="text-red-400 mb-2">⚠️ Failed to load wallet</div>
                    <button
                      onClick={handleWalletRefresh}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      Retry
                    </button>
                  </div>
                ) : walletLoading ? (
                  <div className="text-center py-4">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <div className="text-gray-400 text-sm">Loading wallet...</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Available:</span>
                      <span className="text-green-400 font-bold">${formattedBalances.available}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Locked:</span>
                      <span className="text-yellow-400">${formattedBalances.locked}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-2">
                      <span className="text-gray-300">Total:</span>
                      <span className="text-white font-bold">${formattedBalances.balance}</span>
                    </div>
                  </div>
                )}
                <button 
                  onClick={() => navigate('/wallet')}
                  className="w-full mt-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  Go to Wallet
                </button>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-bold text-white mb-4">📊 Betting Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Bets</span>
                  <span className="text-white font-semibold">{userStats.totalBets}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Win Rate</span>
                  <span className="text-green-400 font-semibold">{userStats.winRate}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Wagered</span>
                  <span className="text-white font-semibold">${(userStats.totalWagered || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Winnings</span>
                  <span className="text-green-400 font-semibold">${(userStats.totalWinnings || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Favorite Sport</span>
                  <span className="text-white font-semibold">{userStats.favoriteSport}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Biggest Win</span>
                  <span className="text-green-400 font-semibold">${(userStats.biggestWin || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="lg:w-2/3">
            {/* Tabs - Mobile Scrollable */}
            <div className="flex overflow-x-auto pb-2 mb-4 sm:mb-6">
              <div className="flex space-x-2 min-w-max">
                {['personal', 'security'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg capitalize text-sm sm:text-base whitespace-nowrap ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {tab === 'personal' && '👤 Personal'}
                    {tab === 'security' && '🔒 Security'}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
              {/* Personal Info Tab */}
              {activeTab === 'personal' && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
                    <h3 className="text-xl font-bold text-white">Personal Information</h3>
                    <div className="flex items-center gap-2">
                      {isEditing && (
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setFormData({
                              full_name: userData.full_name || '',
                              phone_number: userData.phone_number || '',
                              country: userData.country || '',
                              date_of_birth: userData.date_of_birth || ''
                            });
                          }}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                        >
                          <FiX className="w-4 h-4 inline mr-2" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                        <FiUser className="inline mr-2" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => handleFormChange('full_name', e.target.value)}
                        disabled={!isEditing}
                        className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                        <FiMail className="inline mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={userData.email || ''}
                        disabled
                        className="w-full bg-gray-800 text-gray-400 px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none cursor-not-allowed"
                      />
                      {!userData.email_verified && (
                        <div className="mt-2 text-sm">
                          <span className="text-red-400">Email not verified. </span>
                          <button 
                            onClick={() => navigate('/verify-email')}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Verify now
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                        <FiPhone className="inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone_number}
                        onChange={(e) => handleFormChange('phone_number', e.target.value)}
                        disabled={!isEditing}
                        className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                        <FiCalendar className="inline mr-2" />
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => handleFormChange('date_of_birth', e.target.value)}
                        disabled={!isEditing}
                        className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                        <FiGlobe className="inline mr-2" />
                        Country
                      </label>
                      <select
                        value={formData.country}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                        disabled={!isEditing}
                        className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                      >
                        <option value="">Select Country</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Spain">Spain</option>
                        <option value="Italy">Italy</option>
                      </select>
                    </div>
                  </div>

                  {/* KYC Section */}
                  <div className="mt-8">
                    <h4 className="text-lg font-bold text-white mb-4">📋 KYC Verification</h4>
                    <div className={`p-4 rounded-lg ${
                      userData.kyc_status === 'verified' ? 'bg-green-900/30 border border-green-800/30' :
                      userData.kyc_status === 'pending' ? 'bg-yellow-900/30 border border-yellow-800/30' :
                      userData.kyc_status === 'rejected' ? 'bg-red-900/30 border border-red-800/30' :
                      'bg-gray-800'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h5 className="font-bold text-white mb-1">
                            {userData.kyc_status === 'verified' ? '✅ Verified' :
                             userData.kyc_status === 'pending' ? '⏳ Pending Review' :
                             userData.kyc_status === 'rejected' ? '❌ Rejected' :
                             '📝 Not Submitted'}
                          </h5>
                          <p className="text-gray-300 text-sm">
                            {userData.kyc_status === 'verified' ? 'Your identity has been verified.' :
                             userData.kyc_status === 'pending' ? 'Your KYC submission is under review.' :
                             userData.kyc_status === 'rejected' ? 'Your KYC submission was rejected.' :
                             'Complete KYC verification for higher limits and withdrawals.'}
                          </p>
                        </div>
                        <button
                          onClick={() => navigate('/kyc')}
                          className={`px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                            userData.kyc_status === 'verified' ? 'bg-green-600 hover:bg-green-700 text-white' :
                            userData.kyc_status === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700 text-white' :
                            userData.kyc_status === 'rejected' ? 'bg-red-600 hover:bg-red-700 text-white' :
                            'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {userData.kyc_status === 'verified' ? 'View Status' :
                           userData.kyc_status === 'pending' ? 'Check Status' :
                           userData.kyc_status === 'rejected' ? 'Resubmit' :
                           'Start KYC'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-6">Security Settings</h3>
                  
                  <div className="space-y-6">
                    {/* Change Password */}
                    <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
                      <h4 className="font-bold text-white mb-4">Change Password</h4>
                      <div className="space-y-4">
                        {/* Current Password */}
                        <div>
                          <label className="block text-gray-300 mb-2 text-sm">Current Password</label>
                          <div className="relative">
                            <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={passwordForm.currentPassword}
                              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                              className={`w-full pl-12 pr-12 py-3 bg-gray-900 rounded-lg border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-700'
                              }`}
                              placeholder="Enter current password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                            >
                              {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                          </div>
                          {passwordErrors.currentPassword && (
                            <p className="mt-1 text-sm text-red-400">{passwordErrors.currentPassword}</p>
                          )}
                        </div>

                        {/* New Password */}
                        <div>
                          <label className="block text-gray-300 mb-2 text-sm">New Password</label>
                          <div className="relative">
                            <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={passwordForm.newPassword}
                              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                              className={`w-full pl-12 pr-12 py-3 bg-gray-900 rounded-lg border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                passwordErrors.newPassword ? 'border-red-500' : 'border-gray-700'
                              }`}
                              placeholder="Enter new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                            >
                              {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                          </div>
                          {passwordErrors.newPassword && (
                            <p className="mt-1 text-sm text-red-400">{passwordErrors.newPassword}</p>
                          )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                          <label className="block text-gray-300 mb-2 text-sm">Confirm New Password</label>
                          <div className="relative">
                            <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={passwordForm.confirmPassword}
                              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                              className={`w-full pl-12 pr-12 py-3 bg-gray-900 rounded-lg border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-700'
                              }`}
                              placeholder="Confirm new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                            >
                              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                          </div>
                          {passwordErrors.confirmPassword && (
                            <p className="mt-1 text-sm text-red-400">{passwordErrors.confirmPassword}</p>
                          )}
                        </div>

                        <button
                          onClick={handlePasswordSubmit}
                          disabled={saving}
                          className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? (
                            <div className="flex items-center justify-center gap-3">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Updating Password...
                            </div>
                          ) : (
                            'Update Password'
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Logout Button */}
                    <div>
                      <button
                        onClick={logout}
                        className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        <FiLogOut className="w-5 h-5" />
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30">
        <div className="flex justify-around">
          <button
            onClick={() => setActiveTab('personal')}
            className={`flex flex-col items-center ${activeTab === 'personal' ? 'text-blue-400' : 'text-gray-400'} hover:text-white`}
          >
            <FiUser className="w-5 h-5 mb-1" />
            <span className="text-xs">Profile</span>
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex flex-col items-center ${activeTab === 'security' ? 'text-green-400' : 'text-gray-400'} hover:text-white`}
          >
            <FiShield className="w-5 h-5 mb-1" />
            <span className="text-xs">Security</span>
          </button>
        </div>
      </div>

      {/* Add padding for mobile bottom navigation */}
      <div className="pb-20 lg:pb-0"></div>

      <Footer />
    </div>
  );
};

export default ProfilePage;